import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFormConfigurations } from '@/hooks/useFormConfigurations';
import { useSubmissions } from '@/hooks/useSubmissions';
import ConfigurableForm, { resolveOtherAnswer } from '@/components/base/ConfigurableForm';
import type { FormAnswers } from '@/components/base/ConfigurableForm';
import type { EPIRequestItem } from '@/types';

export default function ColaboradorEPI() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { getForm } = useFormConfigurations();
  const { submitEPI } = useSubmissions();

  const config = getForm('epi');

  const initialValues: FormAnswers = {
    'q-epi-1': user?.name || '',
  };

  const handleSubmit = async (answers: FormAnswers) => {
    setSubmitting(true);
    setError('');

    try {
      const tableData = (answers['q-epi-4'] as Record<string, Record<string, boolean>>) || {};
      const itens: { tipo: string; q01: number; q02: number; qCaixa: number; outroDescricao?: string }[] = Object.entries(tableData)
        .filter(([, cols]) => Object.values(cols).some(Boolean))
        .map(([tipo, cols]) => ({
          tipo,
          q01: cols['01'] ?1 : 0,
          q02: cols['02'] ?1 : 0,
          qCaixa: cols['Caixa'] ?1 : 0,
          outroDescricao: tipo === 'Outro' ?(answers['q-epi-4-outro'] as string) || '' : undefined,
        }));

      if (itens.length === 0) {
        setError('Selecione pelo menos um EPI na tabela de quantidades.');
        setSubmitting(false);
        return;
      }

      await submitEPI({
        setor: resolveOtherAnswer(answers, 'q-epi-2') || user?.setor || '',
        motivo: resolveOtherAnswer(answers, 'q-epi-3') || '',
        urgencia: (answers['q-epi-6'] as string) || '',
        observacao: (answers['q-epi-7'] as string) || '',
        itens,
      });

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : typeof err === 'object' && err && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Erro ao enviar solicitação. Tente novamente.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-portal-gray-500">Formulário não configurado.</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div>
        <div className="bg-white rounded-xl border border-portal-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-green-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-portal-gray-900 mb-2">Solicitação Enviada!</h2>
          <p className="text-sm text-portal-gray-500 mb-6">
            Registramos sua solicitação no sistema e retornaremos assim que possível.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setSubmitted(false); }}
              className="px-4 py-2.5 bg-portal-purple text-white text-sm font-medium rounded-lg hover:bg-portal-purple-dark transition-colors whitespace-nowrap"
            >
              Nova Solicitação
            </button>
            <button
              onClick={() => navigate('/colaborador/historico')}
              className="px-4 py-2.5 border border-portal-gray-300 text-portal-gray-700 text-sm font-medium rounded-lg hover:bg-portal-gray-50 transition-colors whitespace-nowrap"
            >
              Ver Histórico
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-bold text-portal-gray-900 mb-2">Solicitação de EPI</h1>
      <p className="text-sm text-portal-gray-500 mb-6">
        Preencha o formulário para solicitar novos equipamentos de proteção individual.
      </p>

      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden mb-4">
        <div className="bg-portal-purple px-6 py-4">
          <h2 className="text-white font-semibold text-sm">Registrar Pedido</h2>
        </div>
        <div className="p-6">
          <img
            src="https://readdy.ai/api/search-image?query=Industrial%20safety%20equipment%20banner%20with%20hard%20hat%20gloves%20boots%20goggles%20on%20clean%20white%20background%20professional%20corporate%20style%20dark%20blue%20and%20gray%20tones%20editorial%20photography&width=800&height=200&seq=epi-banner-supabase&orientation=landscape"
            alt="Solicitação de EPI"
            className="w-full h-40 object-cover rounded-lg"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
          <span>{error}</span>
        </div>
      )}

      <ConfigurableForm
        config={config}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitLabel={submitting ?'Enviando...' : 'Enviar Solicitação'}
        cancelLabel="Voltar"
        onCancel={() => navigate('/colaborador')}
        disabled={submitting}
      />
    </div>
  );
}
